import {
  VoiceProfileDetailApi,
  VoiceProfileCreateApi,
  VoiceProfileListItemApi,
  VoiceProfileUpdateApi,
  RealtimeSessionShortApi,
  VoiceOptionApi,
} from '../Services/VoiceProfileApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';

// ============================================================
// Types
// ============================================================

export type VoiceProfileForm = {
  name: string;
  assistantKey: string;
  voice: string;
  voiceSpeed: number;
  vadThreshold: number;
  vadSilence: number;
  stability: number;
  similarityBoost: number;
  languageCode: string;
  secondaryLanguageCodes: string[];
  filterBackgroundAudio: boolean;
  isActive: boolean;
  greetingMessage: string;
};

type Agent = {
  key: string;
  name: string;
};

export type VoiceOption = {
  value: string;
  label: string;
  previewUrl?: string | null;
};

export type VoiceProfileFormProps = {
  form: VoiceProfileForm;
  agents: Agent[];
  voices: VoiceOption[];
  languages: VoiceOption[];
  isDisabled?: boolean;
  voicesLoading?: boolean;
  onRefreshVoices?: () => void;
  onChange: (form: VoiceProfileForm) => void;
};

// ============================================================
// Constants
// ============================================================

export const INITIAL_FORM_STATE: VoiceProfileForm = {
  name: '',
  assistantKey: '',
  voice: '',
  voiceSpeed: 1,
  vadThreshold: 0.5,
  vadSilence: 1,
  stability: 0.4,
  similarityBoost: 0.75,
  languageCode: 'ukr',
  secondaryLanguageCodes: [],
  filterBackgroundAudio: false,
  isActive: true,
  greetingMessage: '',
};

export const VOICE_PROFILE_FIELD_MAP: Record<keyof VoiceProfileForm, keyof VoiceProfileDetailApi> =
  {
    name: 'name',
    assistantKey: 'assistant_key',
    voice: 'voice',
    voiceSpeed: 'voice_speed',
    vadThreshold: 'vad_threshold',
    vadSilence: 'vad_silence',
    stability: 'stability',
    similarityBoost: 'similarity_boost',
    languageCode: 'language_code',
    secondaryLanguageCodes: 'secondary_language_codes',
    filterBackgroundAudio: 'filter_background_audio',
    isActive: 'is_active',
    greetingMessage: 'greeting_message',
  };

// Fields that should be sent as `null` when empty (nullable string fields)
const NULLABLE_STRING_FIELDS: (keyof VoiceProfileForm)[] = [
  'assistantKey',
  'voice',
  'greetingMessage',
];

// ============================================================
// Table columns
// ============================================================

export const voiceProfileColumns = [
  { name: 'ID', selector: (row: VoiceProfileListItemApi) => row.key, sortable: false },
  { name: 'Name', selector: (row: VoiceProfileListItemApi) => row.name, sortable: false },
  {
    name: 'Status',
    selector: (row: VoiceProfileListItemApi) => (row.is_active ? 'Active' : 'InActive'),
    sortable: false,
  },
  {
    name: 'Created',
    selector: (row: VoiceProfileListItemApi) => formatDateTimeLocal(row.created_at),
    sortable: false,
  },
  {
    name: 'Updated',
    selector: (row: VoiceProfileListItemApi) => formatDateTimeLocal(row.updated_at),
    sortable: false,
  },
];

// ============================================================
// Session helpers
// ============================================================

export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
};

export const sessionColumns = [
  { name: 'ID', selector: (row: RealtimeSessionShortApi) => row.key, sortable: false },
  {
    name: 'Created',
    selector: (row: RealtimeSessionShortApi) => formatDateTimeLocal(row.created_at),
    sortable: false,
  },
  {
    name: 'Duration',
    selector: (row: RealtimeSessionShortApi) => formatDuration(row.duration_seconds),
    sortable: false,
  },
];

// ============================================================
// Validation
// ============================================================

export const validate = (
  form: VoiceProfileForm,
  showAlert: (type: 'fail' | 'success', message: string) => void,
): boolean => {
  const checks: [boolean, string][] = [[!form.name.trim(), 'Voice profile name is required']];

  const failed = checks.find(([condition]) => condition);
  if (failed) {
    showAlert('fail', failed[1]);
    return false;
  }

  return true;
};

// ============================================================
// Form <-> API mapping
// ============================================================

const toApiValue = (
  formKey: keyof VoiceProfileForm,
  value: VoiceProfileForm[keyof VoiceProfileForm],
) => {
  if (NULLABLE_STRING_FIELDS.includes(formKey)) {
    return (value as string) || null;
  }
  return value;
};

export const mapFormToApi = (form: VoiceProfileForm): VoiceProfileCreateApi => {
  return Object.entries(VOICE_PROFILE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    const key = formKey as keyof VoiceProfileForm;
    (acc as Record<string, unknown>)[apiKey] = toApiValue(key, form[key]);
    return acc;
  }, {} as VoiceProfileCreateApi);
};

// Array-valued fields (secondary languages) are rebuilt on every edit, so a
// reference comparison would report them as changed on every save.
const isUnchanged = (
  a: VoiceProfileForm[keyof VoiceProfileForm],
  b: VoiceProfileForm[keyof VoiceProfileForm],
): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
};

export const getChangedFields = (
  initial: VoiceProfileForm,
  current: VoiceProfileForm,
): VoiceProfileUpdateApi => {
  return Object.entries(VOICE_PROFILE_FIELD_MAP).reduce((acc, [formKey, apiKey]) => {
    const key = formKey as keyof VoiceProfileForm;
    if (!isUnchanged(current[key], initial[key])) {
      (acc as Record<string, unknown>)[apiKey] = toApiValue(key, current[key]);
    }
    return acc;
  }, {} as VoiceProfileUpdateApi);
};

export const mapVoiceOptions = (voices: VoiceOptionApi[], selected?: string): VoiceOption[] => {
  const options = voices.map((voice) => ({
    value: voice.id,
    label: voice.name,
    previewUrl: voice.preview_url,
  }));

  // A voice that is no longer on offer, such as one saved before voices came
  // from ElevenLabs, stays selectable so that saving does not drop it.
  if (selected && !options.some((option) => option.value === selected)) {
    options.unshift({ value: selected, label: selected, previewUrl: null });
  }
  return options;
};
